// Editor thumbnail Shorts (2026-10-08). Gambar dasar = frame dari video yang dipilih (diambil di
// browser, tanpa unggah video) atau foto unggahan. Pratinjau dirender server dengan mesin yang sama
// dengan hasil akhir (lib/shorts-thumb.js), jadi yang terlihat = yang dipasang ke YouTube.
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { colors, radius } from '../theme';

const API = process.env.REACT_APP_API_URL || 'https://api.sm-media.id';

const FONTS = [
  { id: 'bebas', label: 'Bebas Neue' },
  { id: 'anton', label: 'Anton' },
  { id: 'poppins', label: 'Poppins' },
  { id: 'montserrat', label: 'Montserrat' },
  // 2026-10-16: font display tambahan (dirender server; label hanya nama)
  { id: 'bangers', label: 'Bangers' },
  { id: 'archivo', label: 'Archivo Black' },
  { id: 'bungee', label: 'Bungee' },
  { id: 'blackops', label: 'Black Ops' },
  { id: 'russo', label: 'Russo One' },
  { id: 'righteous', label: 'Righteous' },
  { id: 'oswald', label: 'Oswald' },
  { id: 'teko', label: 'Teko' },
  { id: 'lobster', label: 'Lobster' },
  { id: 'pacifico', label: 'Pacifico' },
];
const POSITIONS = [{ id: 'atas', label: 'Atas' }, { id: 'tengah', label: 'Tengah' }, { id: 'bawah', label: 'Bawah' }];
const STYLES = [
  { id: 'bayangan', label: 'Bayangan tegas' }, { id: 'lembut', label: 'Bayangan lembut' }, { id: 'neon', label: 'Neon' },
  { id: 'outline2', label: 'Outline ganda' }, { id: '3d', label: 'Timbul 3D' }, { id: 'gradasi', label: 'Gradasi' },
  { id: 'kotak', label: 'Kotak sorot' }, { id: 'garis', label: 'Garis tepi' },
];
const ALIGNS = [{ id: 'tengah', label: 'Tengah' }, { id: 'kiri', label: 'Kiri' }];
const SIZES = [{ id: 'normal', label: 'Normal' }, { id: 'besar', label: 'Besar' }, { id: 'ekstra', label: 'Ekstra besar' }];
const FITS = [{ id: 'auto', label: 'Otomatis' }, { id: 'penuh', label: 'Penuh' }, { id: 'blur', label: 'Latar blur' }];

const label = { fontSize: 12, fontWeight: 600, color: colors.textSecondary, marginBottom: 4, display: 'block' };
const field = { width: '100%', boxSizing: 'border-box', fontSize: 13 };
// input warna diberi ukuran eksplisit: gaya input global dasbor membuatnya tampil sebagai garis tipis.
const swatch = { width: 48, height: 34, padding: 2, border: `1px solid ${colors.border}`, borderRadius: 8, background: colors.bgCard, cursor: 'pointer', display: 'block', marginTop: 4 };

function Seg({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map(o => (
        <button key={o.id} type="button" onClick={() => onChange(o.id)}
          style={{ padding: '6px 10px', fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
            border: `1px solid ${value === o.id ? colors.accent : colors.border}`,
            background: value === o.id ? colors.accentMuted : colors.bgCard,
            color: value === o.id ? colors.accentText : colors.textPrimary }}>{o.label}</button>
      ))}
    </div>
  );
}

// Gambar dari video YouTube (form URL, 2026-10-16): thumbnail resmi + 3 frame otomatis YouTube.
// Kotak kecil memakai gambar kecil (cepat & selalu ada); versi penuhnya diambil server saat diklik.
const YT_PILIHAN = [
  { id: 'resmi', img: 'mqdefault', label: 'Thumbnail' },
  { id: 'f1', img: 'hq1', label: '±25%' },
  { id: 'f2', img: 'hq2', label: '±50%' },
  { id: 'f3', img: 'hq3', label: '±75%' },
];
const mmss = s => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const youtubeId = url => (String(url || '').match(/(?:[?&]v=|youtu\.be\/|\/shorts\/|\/embed\/|\/live\/)([A-Za-z0-9_-]{11})(?![A-Za-z0-9_-])/) || [])[1] || null;

function Modal({ videoUrl, youtubeUrl, defaultTitle, initial, onClose, onSaved }) {
  const ytId = videoUrl ? null : youtubeId(youtubeUrl);
  const [ytPilih, setYtPilih] = useState(null);
  // Detik bebas (2026-10-16): durasi dibaca server via yt-dlp, frame diambil ffmpeg di detik pilihan.
  const [ytDur, setYtDur] = useState(0);
  const [ytDetik, setYtDetik] = useState(0);
  const [baseId, setBaseId] = useState(initial?.base_id || null);
  // Thumbnail baru: teks besar di tengah + bayangan tegas. Thumbnail lama (tanpa align/size) tetap kiri/normal
  // supaya hasilnya sama seperti waktu dibuat.
  const [params, setParams] = useState(initial?.params ? { align: 'kiri', size: 'normal', ...initial.params } : {
    title: defaultTitle || '', subtitle: '', font: 'bebas', color: '#FFFFFF', accent: '#FF5722',
    position: 'tengah', style: 'bayangan', align: 'tengah', size: 'besar', darken: 0.25, fit: 'auto', uppercase: true,
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [dur, setDur] = useState(0);
  const [t, setT] = useState(0);
  const videoRef = useRef(null);
  const abortRef = useRef(null);
  const set = (k, v) => setParams(p => ({ ...p, [k]: v }));

  async function uploadBase(blob) {
    setBusy('Menyiapkan gambar...'); setErr('');
    try {
      const fd = new FormData();
      fd.append('image', blob, 'base.jpg');
      const r = await axios.post(`${API}/api/thumbnails/shorts/base`, fd);
      setBaseId(r.data.base_id);
    } catch (e) {
      setErr(e.response?.data?.error || 'Gagal menyiapkan gambar');
    }
    setBusy('');
  }

  async function pickYoutube(frame) {
    setBusy('Mengambil gambar dari YouTube...'); setErr('');
    try {
      const r = await axios.post(`${API}/api/thumbnails/shorts/base-youtube`, { url: youtubeUrl, frame });
      setBaseId(r.data.base_id); setYtPilih(frame);
    } catch (e) {
      setErr(e.response?.data?.error || 'Gagal mengambil gambar dari YouTube');
    }
    setBusy('');
  }

  async function muatDurasi() {
    setBusy('Membaca video dari YouTube… (±15 detik)'); setErr('');
    try {
      const r = await axios.post(`${API}/api/thumbnails/shorts/youtube-info`, { url: youtubeUrl });
      const d = Math.max(1, Number(r.data.duration) || 0);
      setYtDur(d); setYtDetik(Math.floor(d / 2));
    } catch (e) {
      setErr(e.response?.data?.error || 'Gagal membaca video dari YouTube');
    }
    setBusy('');
  }

  async function pickDetik() {
    setBusy(`Mengambil frame detik ${mmss(ytDetik)}… (±10 detik)`); setErr('');
    try {
      const r = await axios.post(`${API}/api/thumbnails/shorts/base-youtube-time`, { url: youtubeUrl, sec: ytDetik });
      setBaseId(r.data.base_id); setYtPilih('detik');
    } catch (e) {
      setErr(e.response?.data?.error || 'Gagal mengambil frame dari YouTube');
    }
    setBusy('');
  }

  function grabFrame() {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return setErr('Video belum siap, tunggu sebentar');
    const c = document.createElement('canvas');
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    c.toBlob(b => b ? uploadBase(b) : setErr('Gagal mengambil frame'), 'image/jpeg', 0.92);
  }

  // Pratinjau: dirender server, ditunda 400 ms setelah perubahan terakhir.
  useEffect(() => {
    if (!baseId) return undefined;
    const h = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      try {
        const r = await axios.post(`${API}/api/thumbnails/shorts/render`, { base_id: baseId, params, save: false },
          { responseType: 'blob', signal: ac.signal });
        setPreview(old => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(r.data); });
        setErr('');
      } catch (e) {
        if (axios.isCancel?.(e) || e.name === 'CanceledError') return;
        setErr('Pratinjau gagal dimuat');
      }
    }, 400);
    return () => clearTimeout(h);
  }, [baseId, params]);

  async function save() {
    if (!baseId) return setErr('Pilih gambar dasar dulu');
    setBusy('Menyimpan...');
    try {
      const r = await axios.post(`${API}/api/thumbnails/shorts/render`, { base_id: baseId, params, save: true });
      onSaved({ thumb_id: r.data.thumb_id, url: r.data.url, base_id: baseId, params });
    } catch (e) {
      setErr(e.response?.data?.error || 'Gagal menyimpan thumbnail');
      setBusy('');
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(17,24,39,0.55)', overflowY: 'auto', padding: '24px 16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ maxWidth: 880, margin: '0 auto', background: colors.bgCard, borderRadius: radius.lg, padding: 20, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: colors.textPrimary }}>Thumbnail Shorts</h3>
          <button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textSecondary }}>×</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
          <div style={{ flex: '0 1 270px', minWidth: 0, margin: '0 auto' }}>
            <div style={{ width: '100%', maxWidth: 270, aspectRatio: '9 / 16', borderRadius: radius.md, overflow: 'hidden',
              background: colors.bgCardMuted, border: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              {preview
                ? <img src={preview} alt="Pratinjau thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                : <div style={{ fontSize: 12, color: colors.textMuted, padding: 16, textAlign: 'center' }}>Pilih frame video atau unggah gambar untuk mulai</div>}
            </div>
            <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 8, lineHeight: 1.5, maxWidth: 270 }}>
              Teks dijaga di luar area bawah & kanan yang tertutup tombol Shorts. Ukuran akhir 1080×1920.
            </div>
          </div>

          <div style={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <span style={label}>Gambar dasar</span>
              {videoUrl && (
                <div style={{ marginBottom: 8 }}>
                  <video ref={videoRef} src={videoUrl} muted playsInline preload="auto"
                    onLoadedMetadata={e => { setDur(e.target.duration || 0); e.target.currentTime = Math.min(1, (e.target.duration || 0) / 2); setT(Math.min(1, (e.target.duration || 0) / 2)); }}
                    style={{ width: '100%', maxHeight: 200, background: '#000', borderRadius: 8, display: 'block' }} />
                  <input type="range" min={0} max={dur || 0} step={0.1} value={t} style={{ width: '100%', marginTop: 6 }}
                    onChange={e => { const v = Number(e.target.value); setT(v); if (videoRef.current) videoRef.current.currentTime = v; }} />
                  <button type="button" className="btn btn-outline" style={{ fontSize: 12 }} disabled={!!busy} onClick={grabFrame}>
                    📸 Pakai frame ini ({t.toFixed(1)} dtk)
                  </button>
                </div>
              )}
              {ytId && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 6 }}>Pilih gambar dari video YouTube:</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6 }}>
                    {YT_PILIHAN.map(o => (
                      <button key={o.id} type="button" disabled={!!busy} onClick={() => pickYoutube(o.id)}
                        style={{ padding: 0, border: `2px solid ${ytPilih === o.id ? colors.accent : colors.border}`, borderRadius: 8,
                          background: colors.bgCard, cursor: busy ? 'wait' : 'pointer', overflow: 'hidden', minWidth: 0 }}>
                        <img src={`https://img.youtube.com/vi/${ytId}/${o.img}.jpg`} alt={o.label}
                          style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', display: 'block' }} />
                        <div style={{ fontSize: 10, padding: '3px 2px', color: ytPilih === o.id ? colors.accentText : colors.textSecondary, fontWeight: 600 }}>{o.label}</div>
                      </button>
                    ))}
                  </div>
                  {!ytDur ? (
                    <button type="button" className="btn btn-outline" style={{ fontSize: 12, marginTop: 8 }} disabled={!!busy} onClick={muatDurasi}>⏱️ Pilih detik lain…</button>
                  ) : (
                    <div style={{ marginTop: 10, padding: 10, borderRadius: 8, border: `1px solid ${ytPilih === 'detik' ? colors.accent : colors.border}` }}>
                      <span style={label}>Detik ke-{mmss(ytDetik)} dari {mmss(ytDur)}</span>
                      <input type="range" min={0} max={ytDur} step={1} value={ytDetik} disabled={!!busy}
                        onChange={e => setYtDetik(Number(e.target.value))}
                        style={{ width: '100%', padding: 0, border: 'none', background: 'transparent', boxShadow: 'none' }} />
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                        <label style={{ fontSize: 12, color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>Detik
                          <input type="number" min={0} max={ytDur} step={1} value={ytDetik} disabled={!!busy}
                            onChange={e => setYtDetik(Math.max(0, Math.min(ytDur, Math.floor(Number(e.target.value) || 0))))} style={{ width: 90 }} /></label>
                        <button type="button" className="btn btn-outline" style={{ fontSize: 12 }} disabled={!!busy} onClick={pickDetik}>📸 Ambil frame detik ini</button>
                      </div>
                      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 6, lineHeight: 1.5 }}>Frame diambil langsung dari video YouTube (±10 detik per frame).</div>
                    </div>
                  )}
                </div>
              )}
              <label className="btn btn-outline" style={{ display: 'inline-block', cursor: 'pointer', fontSize: 12 }}>
                🖼️ Unggah gambar
                <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files[0]; e.target.value = ''; if (f) uploadBase(f); }} />
              </label>
              {!videoUrl && !ytId && <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 6 }}>{youtubeUrl !== undefined
                ? 'Isi link YouTube di form untuk memilih gambar langsung dari videonya.'
                : 'Pilih file video dulu untuk mengambil frame langsung dari videonya.'}</div>}
            </div>

            <div>
              <span style={label}>Judul ({params.title.length}/90)</span>
              <textarea rows={2} maxLength={90} value={params.title} onChange={e => set('title', e.target.value)} style={{ ...field, resize: 'vertical' }} />
            </div>
            <div>
              <span style={label}>Sub judul (opsional)</span>
              <input maxLength={40} value={params.subtitle} onChange={e => set('subtitle', e.target.value)} style={field} />
            </div>
            <div><span style={label}>Font</span><Seg options={FONTS} value={params.font} onChange={v => set('font', v)} /></div>
            <div><span style={label}>Gaya teks</span><Seg options={STYLES} value={params.style} onChange={v => set('style', v)} /></div>
            <div><span style={label}>Posisi teks</span><Seg options={POSITIONS} value={params.position} onChange={v => set('position', v)} /></div>
            <div><span style={label}>Rata teks</span><Seg options={ALIGNS} value={params.align} onChange={v => set('align', v)} /></div>
            <div><span style={label}>Ukuran teks</span><Seg options={SIZES} value={params.size} onChange={v => set('size', v)} /></div>
            <div><span style={label}>Tata latar</span><Seg options={FITS} value={params.fit} onChange={v => set('fit', v)} /></div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
              <label style={{ fontSize: 12, color: colors.textSecondary }}>Warna teks<br />
                <input type="color" value={params.color} onChange={e => set('color', e.target.value)} style={swatch} /></label>
              <label style={{ fontSize: 12, color: colors.textSecondary }}>Warna sorot<br />
                <input type="color" value={params.accent} onChange={e => set('accent', e.target.value)} style={swatch} /></label>
              {/* warna sorot dipakai juga untuk glow neon, sisi 3D, outline ganda & ujung gradasi */}
              <label style={{ fontSize: 12, color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" style={{ width: 'auto' }} checked={params.uppercase} onChange={e => set('uppercase', e.target.checked)} /> HURUF BESAR</label>
            </div>
            <div>
              <span style={label}>Gelapkan latar ({Math.round(params.darken * 100)}%)</span>
              <input type="range" min={0} max={0.7} step={0.05} value={params.darken} onChange={e => set('darken', Number(e.target.value))} style={{ width: '100%' }} />
            </div>

            {err && <div style={{ fontSize: 12, color: colors.danger }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              <button type="button" className="btn btn-primary" disabled={!!busy || !baseId} onClick={save}>{busy || 'Simpan thumbnail'}</button>
              <button type="button" className="btn btn-outline" onClick={onClose}>Batal</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** value: {thumb_id, url, base_id, params} | null. onChange(value|null). */
export default function ShortsThumbEditor({ videoUrl, youtubeUrl, defaultTitle, value, onChange, hint }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: 12, marginBottom: 12, background: colors.bgCardMuted }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {value?.url && (
          <img src={`${API}${value.url}`} alt="Thumbnail Shorts" style={{ width: 54, height: 96, objectFit: 'cover', borderRadius: 6, border: `1px solid ${colors.border}` }} />
        )}
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>Thumbnail Shorts {value ? '✅' : '(opsional)'}</div>
          <div style={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.5 }}>
            {hint || 'Dipasang otomatis ke tiap video YouTube setelah upload. Channel harus sudah terverifikasi (nomor HP) agar YouTube menerima thumbnail kustom.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => setOpen(true)}>{value ? 'Ubah' : '+ Buat thumbnail'}</button>
          {value && <button type="button" className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => onChange(null)}>Hapus</button>}
        </div>
      </div>
      {open && (
        <Modal videoUrl={videoUrl} youtubeUrl={youtubeUrl} defaultTitle={defaultTitle} initial={value}
          onClose={() => setOpen(false)} onSaved={v => { onChange(v); setOpen(false); }} />
      )}
    </div>
  );
}
